// İSG Portal — Doküman Analizi (doc-check — supplier self-service) API çağrıları + tipleri.
//
// KAYNAK: api-contract.md §20 (Doküman Analizi). Backend kanonu; burada **varsayım YOK** —
// endpoint yolları, istek/yanıt alanları ve durum kuralları doğrudan sözleşmeden ve S17
// planının §2.1'inde `backend/src/**`'e karşı doğrulanmış sözleşmeden türetilmiştir.
// Serileştirme camelCase'dir (§1); `fields` sözlüğünün ANAHTARLARI bunun tek istisnasıdır
// (§20 — doc-check'in kendi alan kimlikleri, veridir, property adı değildir).
//
// **Kapsam.** Taşeron yetkilisinin §8 akışıyla zaten yüklediği dokümanları doc-check'e
// gönderip sınıflandırma + alan çıkarımı sonucunu sorgulaması (§20.1), sonucu **kabul**
// ederek gerçek §13.2/§15 belge satırlarına dönüştürmesi (§20.2, YENİ S19) ve kabul bekleyen
// / devam eden analizlerini bulması (§20.3, YENİ S19). **Analizin kendisi** (§20.1) hiçbir
// belge satırı yazmaz — yazan TEK uç §20.2'dir ve orada her değer İNSAN ONAYLIDIR: sunucu
// `type`'ı `items[].entity`/`code`'dan yeniden TÜRETMEZ (§20, #33).
//
// **Tenant + contractor izolasyonu.** §13.6 ile aynı kural: contractor kimliği istemciden
// ASLA gönderilmez, aktif firmadaki `SupplierUserCompany.ContractorId` bağından sunucu
// çözer. Başka firmanın/taşeronun analizi hiçbir koşulda görünmez (yok gibi
// `404 DOCUMENT_ANALYSIS_NOT_FOUND`).
//
// **Asenkron sözleşme (§20.1).** Başlatma `202` döner; kimlik `value.uuid`, yoklama aralığı
// `value.pollAfterSeconds` (SANİYE). İstemci durum ucunu `status` terminal (`Succeeded` |
// `Failed`) olana kadar yoklar; terminalde `pollAfterSeconds` `null`'dır. İstemci kendi
// zaman aşımını icat etmez (bkz. plan S17-D6 + `useDocumentAnalysis` hook'u).
//
// **Kabul (§20.2, YENİ S19).** `200` döner, `201` DEĞİL — bir batch'in okunacak tek bir
// `value.uuid`'i yoktur (§4a). **Kısmi başarı VERİDİR, zarf hatası DEĞİLDİR**: her kalemi
// başarısız olan bir istek bile `isSuccess: true` döner; çağıran `results[].outcome`'a göre
// dallanır, HTTP durum koduna göre DEĞİL.

import { api } from './client';

/**
 * Analiz durumu — wire değerleri PascalCase'dir (§20 `DocumentAnalysisDto.status`).
 * Terminal durumlar `Succeeded` / `Failed`.
 */
export type DocumentAnalysisStatus = 'Queued' | 'Running' | 'Succeeded' | 'Failed';

/**
 * Kaynak dokümanın işlenme durumu (§20 `DocumentAnalysisSourceDto.status`) — küçük-harf.
 * `null` = henüz işlenmedi (analiz terminal değil).
 */
export type DocumentAnalysisSourceStatus = 'processed' | 'skipped-known';

/**
 * Bir alanın ayrıştırma sonucu (§20 `DocumentAnalysisFieldDto.parse`). `ok` dışındakiler
 * D-K/S17-D9'un `needsReview` sinyaline katkı yapar — istemci bu durumda `normalized`
 * yerine `raw`'ı uyarı görünümüyle gösterir.
 */
export type DocumentAnalysisFieldParse =
  | 'ok'
  | 'not-normalized'
  | 'date-unparseable'
  | 'tc-checksum-fail'
  | 'tc-malformed';

/**
 * Kişi (subject) bağlanma gerekçesi (§20 `DocumentAnalysisSubjectDto.cohesion`). `null` =
 * tek kalemli/bağlanmamış kişi.
 */
export type DocumentAnalysisCohesion = 'tc-exact' | 'name-normalized';

/** Bulgu önem derecesi (§20 `DocumentAnalysisIssueDto.severity`). */
export type DocumentAnalysisIssueSeverity = 'fail' | 'warn';

/**
 * İşleyiş bulgusunun YALIN özeti — api-contract §20, YENİ. İki ayrı yerde `issues` adıyla
 * görünür: `DocumentAnalysisItemDto.issues` (aşağıda) ve `DocumentAnalysisDto.issues` (hemen
 * altta) — ikisinin ne taşıdığı YERLEŞTİĞİ yere göre değişir, bkz. o iki alanın kendi doc
 * yorumu. `DocumentAnalysisIssueDto`nin (analiz-detayının DEĞİŞMEYEN kardeş dizisi
 * `DocumentAnalysisDetailDto.issues`, aşağıda) AKSİNE burada yalnız `code` + `severity`
 * taşınır — konum alanları (`itemUuid`/`subjectId`/`sourceDocumentUuid`/`pageNumber`) YOKTUR,
 * zaten YERLEŞTİĞİ yer kimliği taşır. `code` aynı desende UNPREFIXED gelir; çağıran taraf
 * `DOCCHECK_` ekleyip §6.1 `/resources`'tan çözer (kod kümesi KAPALI ve DOKUZDUR — D-L/S16-D16,
 * S24'te yeniden onaylandı). ⚠️ Bir `message` alanı YOKTUR ve hiçbir zaman EKLENMEYECEKTİR —
 * metin ASLA telden gelmez, yalnız kod + önem derecesi.
 */
export interface DocumentAnalysisItemIssueDto {
  code: string;
  severity: DocumentAnalysisIssueSeverity;
}

/**
 * Analiz durum satırı — api-contract §20 (`DocumentAnalysisDto`). `POST` (202) ve `GET`
 * (200, `detail.analysis` altında) yanıtlarının İKİSİNDE de aynı şekilde döner.
 *
 * `pollAfterSeconds` SANİYE cinsindendir (`refetchInterval` MİLİSANİYE ister — bu dosya
 * dönüşümü yapmaz, çağıran taraf yapar; bkz. `useDocumentAnalysis`). `Queued`/`Running`'de
 * dolu, terminalde (`Succeeded`/`Failed`) kesin `null`'dır.
 *
 * ⚠️ `failureCode` **zaten `DOCCHECK_` ön ekiyle** gelir (örn. `DOCCHECK_UNAVAILABLE`) —
 * `issues[].code`'un aksine, ikinci kez ön ek eklenirse anahtar çözülemez (S17-D5).
 */
export interface DocumentAnalysisDto {
  uuid: string;
  status: DocumentAnalysisStatus;
  /** En az bir sayfa/dosya/doküman işlenemedi; sonuç yine de kullanılabilir. */
  partial: boolean;
  sourceCount: number;
  pageCount: number;
  documentCount: number;
  skippedCount: number;
  /** Terminal olana kadar `0`. */
  issueCount: number;
  /**
   * YENİ (S19). `items[]` içinde **kabul edilmiş** kalem sayısı. `acceptedItemCount +
   * pendingItemCount == items.length`. `Succeeded` olmayan analizde (`items` boş dizi) ve
   * başlatma yanıtında `0`. `documentCount` ile bir eşitlik VAAT EDİLMEZ — istemci
   * `documentCount`'u kabul ilerlemesi için payda olarak KULLANMAZ.
   */
  acceptedItemCount: number;
  /** YENİ (S19). `items[]` içinde kabul BEKLEYEN kalem sayısı — aynı değişmezler yukarıdaki gibi. */
  pendingItemCount: number;
  /**
   * YENİ (S19). Analiz bir **personel yüzeyinden** başlatıldıysa o personelin uuid'i, aksi
   * `null` (§20.1). **Bağlayıcı DEĞİLDİR** — yalnız §20.2'de kişi seçiminin varsayılanını
   * belirler; sunucu bunu kalem bazındaki `userUuid` ile KARŞILAŞTIRMAZ.
   */
  subjectUserUuid: string | null;
  /** Yalnız `status === 'Failed'` iken dolu; zaten `DOCCHECK_` ön ekli (yukarıya bakınız). */
  failureCode: string | null;
  /** SANİYE. `Queued`/`Running`'de dolu, terminalde `null`. */
  pollAfterSeconds: number | null;
  /** UTC ISO 8601. */
  queuedAt: string;
  /** UTC ISO 8601; henüz başlamadıysa `null`. */
  startedAt: string | null;
  /** UTC ISO 8601; terminal değilse `null`. */
  completedAt: string | null;
  /**
   * YENİ (S25-A4, §20.6). `IsArchived`'ın **manuel** olarak `true` yapıldığı UTC an;
   * `unarchive`'den sonra ya da hiç arşivlenmemişken `null`. **Yalnız manuel arşivde
   * dolar** — analiz *etkin arşivde* (§20.3 `?status=archived`, türetilmiş durum) olsa
   * bile bu alan bunu YANSITMAZ; istemci arşiv üyeliğini bu alandan değil, hangi listede
   * döndüğünden okur.
   */
  archivedAt: string | null;
  /**
   * YENİ (#33, §20). Analizin koştuğu doc-check tanıma sürümü — tamsayı ≥ 1, değişmez.
   * YALNIZ köken (provenance) bilgisidir; istemci buna göre DALLANMAZ. `null` = henüz
   * talep edilmedi (`Queued`), katalog hiç okunamadan `Failed` oldu ya da #33'ten önce analiz edildi. Her durumda alan bulunur ve
   * analizin ömrü boyunca aynı kalır.
   */
  recognitionRelease: number | null;
  /**
   * YENİ. `items[]` TAŞIMAYAN yanıtlarda (başlatma/liste/kabul-sonucu — atfedilecek bir kalem
   * dizisi zaten yok) analizin **bilinen tüm** bulgularını taşır. `items[]` taşıyan detay
   * yanıtında (`DocumentAnalysisDetailDto.analysis.issues`, aynı tip iç içe) sunucu tarafında
   * **daraltılır** — yalnız TEK bir kaleme atfedilemeyen bulgular kalır (atfedilenler
   * `DocumentAnalysisItemDto.issues`'a taşınmıştır, burada TEKRAR ETMEZ). Daima dizi; hiç
   * bulgu yoksa boş dizi (`null` DEĞİL).
   */
  issues: DocumentAnalysisItemIssueDto[];
}

/**
 * Analize giren bir kaynak dokümanın satırı — api-contract §20 (`DocumentAnalysisSourceDto`).
 * `fileName` §8'deki yükleme adının başlangıçta alınmış kopyasıdır (doc-check'in kendi
 * dosya adını EZMEZ). `storageKey` hiçbir koşulda dönmez (§4b).
 */
export interface DocumentAnalysisSourceDto {
  documentUuid: string;
  fileName: string;
  /** Bayt. */
  size: number;
  ordinal: number;
  /** Görsel dosyada / okunamayan PDF'te `null`. */
  pageCount: number | null;
  /** Henüz işlenmediyse `null`. */
  status: DocumentAnalysisSourceStatus | null;
  /** 64 hane küçük-harf hex, ya da `null`. */
  sha256: string | null;
}

/**
 * `fields` sözlüğünün DEĞERİ — api-contract §20 (`DocumentAnalysisFieldDto`).
 *
 * ⚠️ `raw` / `normalized` GÜVENİLMEZ metindir (S17-D8): saldırganın etkileyebileceği bir
 * belgeden modelin okuduğu metindir, doğrulanmış olmaları HTML-güvenli oldukları anlamına
 * GELMEZ. React'in varsayılan escape'i dışında hiçbir şeye (`dangerouslySetInnerHTML`,
 * markdown, `href`/`src` interpolasyonu, log) verilmez.
 */
export interface DocumentAnalysisFieldDto {
  raw: string;
  normalized: string | null;
  parse: DocumentAnalysisFieldParse;
  /** 0..1. */
  confidence: number;
  /** 1-tabanlı. */
  sourcePage: number;
}

/**
 * `fields` AÇIK bir sözlüktür — sözleşmedeki tek istisna (§20). Anahtarları doc-check'in
 * kendi alan kimlikleridir (`tcKimlikNo`, `soyadiAdi`, `belgeSonGecerlilik`, …) ve VERİDİR,
 * property adı değildir; camelCase'e çevrilmez. Küme kapalı DEĞİLDİR — doc-check yeni bir
 * belge türü öğrendikçe büyür.
 *
 * **S17-D7 — bu Record ASLA sabit anahtar adlarıyla exhaustive switch'lenmez.** Bilinen
 * anahtarlar çeviri + görüntü sırası alır; bilinmeyen anahtar ham haliyle (etiket = anahtar)
 * gösterilir, sessizce kaybolmaz.
 */
export type DocumentAnalysisFields = Record<string, DocumentAnalysisFieldDto>;

/** `isgEgitimValidUntil` şekli — §13'ün `hazardClass` ∈ `{1,2,3}` ile birebir karşılıklı. */
export interface DocumentAnalysisIsgEgitimValidUntil {
  sourceField: string;
  /** `yyyy-MM-dd` (`DateOnly`) — Az Tehlikeli (hazardClass 1). */
  azTehlikeli: string;
  /** `yyyy-MM-dd` — Tehlikeli (hazardClass 2). */
  tehlikeli: string;
  /** `yyyy-MM-dd` — Çok Tehlikeli (hazardClass 3). */
  cokTehlikeli: string;
}

/** Kalemden türetilen ek veri — api-contract §20 (`DocumentAnalysisDerivedDto`). */
export interface DocumentAnalysisDerivedDto {
  /** Yalnız doc-check yayınının İSG eğitim geçerlilik türetmesi bildirdiği türün kaleminde dolu
   * (bugün (personnel, IsgTrainingCertificate), §20); aksi `null`. Otomatik hiçbir
   * belge satırına YAZILMAZ — kabul ayrı bir insan kararıdır. */
  isgEgitimValidUntil: DocumentAnalysisIsgEgitimValidUntil | null;
}

/**
 * Tanıma sonucunun varlığı — api-contract §20 (#33 B.0/B.3). `code` bu entity'nin §27 kayıt kodudur
 * (`personnel` → §15 personel belgesi, `company` → §13.2 taşeron firması belgesi, `vehicle` → §23 araç
 * belgesi); etiket §27 kaydından. §20.2 kabul hedefi (`AcceptDocumentAnalysisTarget`)
 * AYNI sözcükleri kullanır; `vehicle`'ın #17'ye kadar kabul hedefi YOKTUR.
 */
export type AnalysisEntity = 'personnel' | 'vehicle' | 'company';

/**
 * Tanıma sonucu — api-contract §20 `DocumentAnalysisItemDto.entity`/`.code` (#33 B.3). Analiz
 * anında doc-check'in tanıma sürümünden (`DocumentAnalysisDto.recognitionRelease`) SAKLANIR;
 * okuma anında hiçbir katalogdan TÜRETİLMEZ.
 *
 * **İkisi BİRLİKTE dolu ya da BİRLİKTE `null`dır** — ayrımlı birlik, bir tüketici yalnız birini
 * dolu sanamaz. `{ entity: null, code: null }` = tanınmadı (sürümün tanımadığı tür, düşük güven
 * dahil) — olağan "kararı insan verir" durumudur, hata DEĞİLDİR. **Sentinel YOKTUR** — `code`
 * hiçbir koşulda `"Other"` gibi bir dolgu taşımaz.
 *
 * `code` o varlığın platform enum'unun ÜYE ADIDIR (PascalCase). Yazıldığı anda enum üyesidir,
 * ama istemci yine de BİLMEDİĞİ bir kodu tolere eder: etiketi ham kod olarak basar, kabul
 * formunu ÖN-SEÇMEZ. Bağlayıcı DEĞİLDİR — §20.2 isteğindeki insan onaylı `type` bağlayıcıdır,
 * sunucu `type`'ı buradan yeniden türetmez.
 */
export type DocumentAnalysisRecognition =
  | { entity: AnalysisEntity; code: string }
  | { entity: null; code: null };

/**
 * doc-check'in tespit ettiği bir doküman kalemi — api-contract §20 (`DocumentAnalysisItemDto`).
 * Türü `DocumentAnalysisRecognition` (`entity`/`code`) taşır. Sunucu bir tür ETİKETİ
 * göndermez (#33 B.3) — istemci etiketi `(entity, code)`'dan KENDİ kataloğuyla çözer (#7'ye
 * kadar; web-supplier'da `lib/analysisInboxTree.ts` `analysisItemLabel`).
 */
export type DocumentAnalysisItemDto = {
  uuid: string;
  /** 0..1, ya da `null`. */
  confidence: number | null;
  /** Kalemin hangi §8 dokümanından geldiği; eşlenemezse `null`. */
  sourceDocumentUuid: string | null;
  /** Kaynak dosya içindeki 1-tabanlı sayfa numaraları, artan. */
  pages: number[];
  /** Analiz-kapsamlı opak kişi kimliği (`"subj-1"`); kişi çıkarılamadıysa `null`. */
  subjectId: string | null;
  fields: DocumentAnalysisFields;
  derived: DocumentAnalysisDerivedDto;
  /**
   * YENİ. TAM OLARAK bu kaleme atfedilen bulgular (bkz. `DocumentAnalysisItemIssueDto` üstü doc
   * yorumu). Daima dizi; yoksa boş dizi (`null` DEĞİL).
   */
  issues: DocumentAnalysisItemIssueDto[];
  /**
   * `yyyy-MM-dd` ya da `null` — belgenin üzerinde BASILI geçerlilik başlangıcı. Analiz anında
   * doc-check'in `printedValidity`'sinden SAKLANIR (#33 B.3 — artık okuma anında türetilmez).
   * `null` olur: sürüm bu `(entity, code)` için bu slotu bağlamıyorsa (ör. yalnız
   * `validUntil` bağlayan bir tür), bağlı alan bu kalemde okunmadıysa, ya da okundu ama
   * `parse !== 'ok'`ysa — ayrıştırılamayan bir değerden ASLA tahmin edilmez. Öncelik (basılıysa
   * öncelik, yoksa `derived`'den hesapla) İSTEMCİNİN işidir (web-supplier
   * `lib/analysisValidity.ts`).
   */
  printedValidFrom: string | null;
  /** `validUntil` slotu — bkz. {@link DocumentAnalysisItemDto.printedValidFrom} üstü doc. */
  printedValidUntil: string | null;
} & DocumentAnalysisRecognition &
  DocumentAnalysisItemAcceptanceState &
  DocumentAnalysisItemTeachingSignal;

/**
 * Kalemin kabul/ret durumu — api-contract §20 (`DocumentAnalysisItemDto`), YENİ (S25-A1).
 * Önceden yalnız `analysis.acceptedItemCount`/`pendingItemCount`'tan dolaylı çıkarılabilirdi;
 * artık kalemin KENDİSİNDE görünür — bu, sayfa yenilendikten sonra da hayatta kalan bir "kabul
 * edildi" / "reddedildi" görünümünün TEK kaynağıdır.
 *
 * Bir kalem kabul VE ret alanlarını AYNI ANDA dolu **taşıyamaz** — ret, zaten kabul edilmiş bir
 * kalemde `409 DOCUMENT_ANALYSIS_ITEM_ALREADY_ACCEPTED` ile soft-delete'ten önce durur (§20.4).
 */
/**
 * Bir kalemi okuyan çıkarımın **öğretme sinyali** (api-contract §20,
 * `DocumentAnalysisItemDto.droppedCount`/`.duplicateCount`). #33'ten sonra da SAKLANIR ve
 * servis edilir (karar R3): doc-check'in değerlendirmesi (eval) aynı sinyali ölçer.
 *
 * ⚠️ **ÜÇ durumlu, İKİ değil — `null` ile `0`'ı ASLA birleştirme (`?? 0`, `|| 0`,
 * `Math.max(0, …)` YASAK).** `null` = ÖLÇÜLMEDİ: katı (`strict`) çıkarım yolu ikisini de
 * yapısal olarak üretmez, tanınmayan ya da çıkarımı olmayan bir kalemde de hiç çıkarım
 * koşmadığı için `null`'dır. `0` = ÖLÇÜLDÜ ve temiz. `null`'ı `0`'a çevirmek hiç denetlenmemiş
 * bir okumayı "kusursuz" gösterir.
 *
 * web-supplier yalnız `droppedCount > 0` iken inceleme panelinde tek bir "yerleştirilemeyen
 * alan" notu basar (`AnalysisExtractedFields.tsx`, §20 R3 ile meşru); `duplicateCount` hiçbir
 * taşeron ekranında basılmaz.
 */
export interface DocumentAnalysisItemTeachingSignal {
  /** Modelin döndürdüğü ama türün alan listesinde karşılığı olmayan okuma sayısı. */
  droppedCount: number | null;
  /** Zaten yerleşmiş bir anahtarı yeniden bildiren okuma sayısı (ilk okuma kazanır). */
  duplicateCount: number | null;
}

export interface DocumentAnalysisItemAcceptanceState {
  /**
   * §20.2'nin kabulde ürettiği bağ satırının kendi uuid'i —
   * `AcceptDocumentAnalysisItemResultDto.linkUuid` ile **aynı çift**; kalem kabul
   * edilmemişse `null`. Silme bununla yapılır (§13.6 / §15.2 `DELETE …/{linkUuid}`).
   */
  acceptedLinkUuid: string | null;
  /**
   * `acceptedLinkUuid`'in HANGİ bağ tablosunda olduğu (iki tablonun uuid uzayı bağımsızdır);
   * kalem kabul edilmemişse `null`.
   */
  acceptedTarget: AcceptDocumentAnalysisTarget | null;
  /**
   * Kabulde insan onaylı hedef tür — hedefin kendi enum'ının ÜYE ADI (PascalCase,
   * `code` ile AYNI sözlük ama BAĞLAYICIDIR, bir öneri değil gerçekleşmiş karardır);
   * kalem kabul edilmemişse `null`.
   */
  acceptedType: string | null;
  /**
   * `acceptedTarget === 'personnel'` iken belgenin bağlandığı kişinin
   * `ContractorPersonnelDto.uuid`'i; `'company'` ya da kabul edilmemiş kalemde `null`.
   */
  acceptedUserUuid: string | null;
  /**
   * YENİ (S25). Kalemin reddedildiği UTC an (§20.4); reddedilmemiş kalemde `null`. Bu alan
   * yalnız §20.1'in detay ucunda (`GET …/{uuid}`) DOLU görünür — reddedilmiş kalemler global
   * soft-delete filtresi yüzünden diğer hiçbir listede/sayaçta görünmez (§20.3, kabul, vb.).
   */
  rejectedAt: string | null;
}

/**
 * Belgelerden çıkarılan kişi — api-contract §20 (`DocumentAnalysisSubjectDto`). **KVKK.**
 * `subjectId` analiz-kapsamlı opak kimliktir, kalıcı bir kişi kimliği DEĞİLDİR ve iki analiz
 * arasında karşılaştırılamaz.
 *
 * ⚠️ `fullName` / `tcKimlikNo` GÜVENİLMEZ metindir (S17-D8, aynı model-transkripsiyon
 * uyarısı). `tcKimlikNo` yalnız checksum-geçerli okumada doludur (başarısız okuma asla
 * yayımlanmaz — `TC_KIMLIK_UNREADABLE` bulgusu düşer yerine) ve KVKK-hassas kişisel veridir:
 * sorgu dizesine, doküman başlığına ya da analitik çağrısına ASLA konulmaz.
 */
export interface DocumentAnalysisSubjectDto {
  subjectId: string;
  fullName: string | null;
  tcKimlikNo: string | null;
  cohesion: DocumentAnalysisCohesion | null;
  itemUuids: string[];
  /**
   * `reason` ∈ `tc-name-conflict` | `tc-conflict` | `name-only` | `name-fuzzy` (§20) —
   * plan §2.1'i izleyerek `string` tutulur (portala ait çeviri/etiket sözlüğü, sabit union
   * DEĞİL — kapalı liste doc-check tarafında büyüyebilir).
   */
  possibleSameAs: { subjectId: string; reason: string }[];
  /**
   * Kimlik kaynağı kalemin uuid'i. Çözülemediği durumda tüm-sıfır uuid
   * (`00000000-0000-0000-0000-000000000000`) taşır — istemci bunu "bağlantı yok" sayar,
   * o uuid ile `items[]` içinde arama YAPMAZ.
   */
  identityItemUuid: string;
}

/**
 * İşleyiş bulgusu — api-contract §20 (`DocumentAnalysisIssueDto`).
 *
 * ⚠️ `code` UNPREFIXED gelir (örn. `"PAGE_LOW_LEGIBILITY"`) — çağıran taraf `DOCCHECK_`
 * ekleyip §6.1 `/resources`'tan çözer. `analysis.failureCode`'un AKSİNE (o zaten ön ekli
 * gelir) — bu asimetri S17-D5'te not edilmiştir, karıştırılırsa anahtar çözülemez.
 *
 * `code` ∈ `UNCLASSIFIED_PAGES` | `PAGE_PROCESSING_FAILED` | `FILE_PROCESSING_FAILED` |
 * `PAGE_CLASSIFICATION_FAILED` | `DOCUMENT_EXTRACTION_FAILED` | `FILE_FETCH_FAILED` |
 * `POSSIBLE_SAME_SUBJECT` | `PAGE_LOW_LEGIBILITY` | `TC_KIMLIK_UNREADABLE` (§20) — plan
 * §2.1'i izleyerek `string` tutulur, sabit union DEĞİL (aynı gerekçe: çeviri anahtarına
 * çözülen bir kimlik, kontrol akışı değil).
 */
export interface DocumentAnalysisIssueDto {
  code: string;
  severity: DocumentAnalysisIssueSeverity;
  itemUuid: string | null;
  subjectId: string | null;
  sourceDocumentUuid: string | null;
  pageNumber: number | null;
}

/**
 * Analizin bir sayfa görseli — api-contract §20 (`DocumentAnalysisPageDto`). `items` /
 * `subjects` / `issues` gibi detay yanıtının bir KARDEŞİDİR, bir kalemin ALANI DEĞİLDİR —
 * aynı sayfa birden çok kalem tarafından gösterilebilir ve kalem başına kopyalanmaz.
 *
 * Sayfa `(sourceDocumentUuid, pageNumber)` ile adreslenir; kalem zaten ikisini de taşır
 * (`DocumentAnalysisItemDto.sourceDocumentUuid` + `.pages`).
 *
 * ⚠️ **`sourceDocumentUuid` `null` OLABİLİR** (portalın bir kaynağa atfedemediği sayfa) —
 * bu bir JOIN ANAHTARI DEĞİLDİR: istemci bu sayfaları eşlemeye ÇALIŞMAZ, atfedilemeyen
 * her kaleme göstermek yerine SESSİZCE düşürür.
 *
 * `thumbnailUrl` presigned'dır ve 30 dakikada dolar. Bu **her detay okumasında yenilenmez**
 * — detay sorgusu analiz `Succeeded`'a ULAŞTIĞI anda yoklamayı DURDURUR, tam da sayfaların
 * belirdiği an. Süresi dolan bir URL, detay sorgusunu YENİDEN ÇEKEREK kurtarılır; arayüz bunu
 * görselin `onError` işleyicisinden TETİKLER. `null` ise görsel saklanamamıştır ve kalem
 * önizlemesiz render edilir.
 */
export interface DocumentAnalysisPageDto {
  sourceDocumentUuid: string | null;
  pageNumber: number;
  thumbnailUrl: string | null;
  thumbnailUrlExpiresAt: string | null;
}

/**
 * Analiz detayı — api-contract §20 (`GET /supplier/contractor/document-analyses/{uuid}`,
 * `DocumentAnalysisDetailDto`). `items`/`subjects`/`issues`/`pages` KARDEŞTİR — `subjects`,
 * `issues` ve `pages` `items`'ın içine gömülmez. Analiz terminal değilken ve `Failed`'de
 * `items`/`subjects`/`issues`/`pages` boş dizidir (asla `null`).
 *
 * **YENİ (S25).** `items` artık **reddedilmiş kalemleri de içerir** — bu uç, §20'nin geri
 * kalanının (kabul, §20.3'ün `pendingItemCount`/`acceptedItemCount` sayaçları) uyduğu global
 * soft-delete filtresinin TEK istisnasıdır: sayfa yenilendiğinde istemcinin üstü çizili bir
 * satır + `geri al` eylemi (§20.4 `restoreDocumentAnalysisItem`) gösterebilmesi içindir.
 * Reddedilmiş bir kalem yalnız `item.rejectedAt` doluyla ayırt edilir — ayrıca bir `status`
 * alanı yoktur. Reddedilmiş kalemler `analysis.acceptedItemCount`/`analysis.pendingItemCount`'u
 * ETKİLEMEZ (ikisi de yalnız reddedilmemiş kalemler üzerinden sayılır).
 */
export interface DocumentAnalysisDetailDto {
  analysis: DocumentAnalysisDto;
  sources: DocumentAnalysisSourceDto[];
  items: DocumentAnalysisItemDto[];
  subjects: DocumentAnalysisSubjectDto[];
  issues: DocumentAnalysisIssueDto[];
  pages: DocumentAnalysisPageDto[];
}

/**
 * Analiz başlatma isteği — api-contract §20.1 (`POST /supplier/contractor/document-analyses`).
 * `documentUuids` §8'de `confirmed` + çağıranın firmasına ait olmalı; 1..10 (tekrarlar
 * sunucuda teklenir). Biri bile geçersizse istek TÜMÜYLE `404 DOCUMENT_NOT_FOUND` ile
 * reddedilir (all-or-nothing; hangisi olduğu bildirilmez — §4b enumeration önlemi).
 */
export interface StartDocumentAnalysisRequest {
  documentUuids: string[];
  /**
   * YENİ (S19), opsiyonel. Analiz bir **personel yüzeyinden** başlatıldıysa o personelin
   * `ContractorPersonnelDto.uuid`'i. Portal tarafı bir NOTTUR — doc-check'e GÖNDERİLMEZ ve
   * kabulü KISITLAMAZ (§20.2'de bağlayıcı olan kalem bazındaki `userUuid`'dir; sunucu ikisini
   * KARŞILAŞTIRMAZ). Çözülemezse `404 PERSONNEL_NOT_FOUND` (bir `errors[]` girdisi DEĞİLDİR).
   * Alanın **yokluğu** ile JSON `null` **AYNI** anlama gelir ("Belgeler'den başlatıldı") —
   * all-zero uuid (`00000000-…`) hiçbir şeye çözülmez ve bu yüzden `404` verir, `null` gibi ele
   * ALINMAZ.
   */
  subjectUserUuid?: string | null;
}

/**
 * `POST /supplier/contractor/document-analyses` (`update` — analiz başlatma da bir
 * mutasyondur; `create` aksiyonu YOKTUR, §20). Kayıt `status = Queued` ile açılır, işleme
 * arka planda yürür → `202` + `value.uuid` + `value.pollAfterSeconds`. Contractor kimliği
 * istekte YOKTUR — aktif firmadaki bağdan sunucu çözer.
 */
export function startDocumentAnalysis(body: StartDocumentAnalysisRequest) {
  return api.post<DocumentAnalysisDto>('supplier/contractor/document-analyses', { json: body });
}

/**
 * `GET /supplier/contractor/document-analyses/{uuid}` (`get`) — analizin durumu +
 * terminalse sonucu (kaynaklar + kalemler + kişiler + bulgular). Yok / başka firmaya ya da
 * başka taşerona ait → `404 DOCUMENT_ANALYSIS_NOT_FOUND`. **Asla `503` dönmez** (§20 — yalnız
 * başlatma ucu döner).
 */
export function getDocumentAnalysis(uuid: string) {
  // `uuid` rota parametresinden gelir (React Router ÇÖZÜLMÜŞ hâlde verir) — kodlanmadan
  // gömülürse `../..` gibi bir değer yolu normalize edilirken kaçar ve SPA kullanıcının
  // kendi token'ıyla saldırganın seçtiği bir uca GET atar (S17 güvenlik incelemesi F3).
  return api.get<DocumentAnalysisDetailDto>(
    `supplier/contractor/document-analyses/${encodeURIComponent(uuid)}`,
  );
}

// ============================================================================
// 20.2 Sonucun kabulü (`update`) — api-contract §20.2, YENİ (S19)
// ============================================================================
//
// Analiz sonucunu gerçek belge satırlarına dönüştüren TEK uç. Kabul edilen her kalem için
// sunucu §8 dokümanı MINT EDER ve §13.2 (`ContractorDocument`) ya da §15 (`PersonnelDocument`)
// link satırı açar. Kabulden ÖNCE hiçbir kalem hiçbir supplier listesinde görünmez.

/**
 * Kabul kalemi hedefi — api-contract §20.2 (`target`; #33 R1: eski `contractor` → `company`,
 * `AnalysisEntity` ile TEK sözlük). `company` → §13.2 uygunluk belgesi, `personnel` → §15
 * personel belgesi. `vehicle` kabul hedefi DEĞİLDİR (#17'ye kadar).
 */
export type AcceptDocumentAnalysisTarget = 'company' | 'personnel';

/**
 * Kabul isteğinin bir `target: "company"` kalemi — api-contract §20.2
 * (`AcceptDocumentAnalysisItemRequest`). `userUuid` bu şekilde TAŞINAMAZ (alan yok) — TS bu
 * hatayı çağrı noktasında reddeder.
 */
export interface AcceptDocumentAnalysisCompanyItemRequest {
  /** `DocumentAnalysisItemDto.uuid` — BU analizin bir kalemi olmalı. */
  itemUuid: string;
  target: 'company';
  /** `company` entity'sinin AKTİF §27 kayıt kodu (§27.6 — pasif `INACTIVE`, yabancı/bilinmeyen `INVALID`). */
  type: string;
  /** DateOnly (yyyy-MM-dd, §4). Model çıktısı yalnız bir ÖNERİDİR — bağlayıcı olan bu alan. */
  validFrom?: string | null;
  validUntil?: string | null;
  /** Azami 500 karakter (§13.2 `Note` sütun sınırı). Model çıktısıyla ÖN-DOLDURULMAZ. */
  note?: string | null;
}

/**
 * Kabul isteğinin bir `target: "personnel"` kalemi — api-contract §20.2
 * (`AcceptDocumentAnalysisItemRequest`). `userUuid` bu şekilde ATLANAMAZ (zorunlu alan) — TS
 * bu hatayı çağrı noktasında reddeder.
 */
export interface AcceptDocumentAnalysisPersonnelItemRequest {
  itemUuid: string;
  target: 'personnel';
  /** `personnel` entity'sinin AKTİF §27 kayıt kodu (§27.6). */
  type: string;
  /**
   * ZORUNLU — `ContractorPersonnelDto.uuid`, yalnız `kind = "Personnel"` (aktive olmamış bir
   * davetli belge ALAMAZ; kalem `PERSONNEL_NOT_FOUND` ile başarısız olur). `analysis
   * .subjectUserUuid` yalnız VARSAYILANI belirler; bağlayıcı olan budur, sunucu ikisini
   * KARŞILAŞTIRMAZ (§20.1).
   */
  userUuid: string;
  validFrom?: string | null;
  validUntil?: string | null;
  note?: string | null;
}

/**
 * Kabul isteğinin bir kalemi — api-contract §20.2 (`AcceptDocumentAnalysisItemRequest`).
 * `target` üzerinde **GERÇEK bir ayrımlı birliktir (discriminated union)**: bir `company`
 * kalemi `userUuid` TAŞIYAMAZ, bir `personnel` kalemi `userUuid`'i ATLAYAMAZ — ikisi de TS'te
 * çağrı noktasında (derleme zamanında) reddedilir, çalışma zamanında değil.
 */
export type AcceptDocumentAnalysisItemRequest =
  | AcceptDocumentAnalysisCompanyItemRequest
  | AcceptDocumentAnalysisPersonnelItemRequest;

/**
 * `POST …/{uuid}/accept` isteği — api-contract §20.2 (`AcceptDocumentAnalysisRequest`).
 * `items` **1..N** kalem (tek kalemlik istek de geçerlidir); boş → `400 VALIDATION_FAILED`
 * (`items`, `REQUIRED`). Aynı `itemUuid` birden çok kez → `400 VALIDATION_FAILED` (`items`,
 * `INVALID`).
 */
export interface AcceptDocumentAnalysisRequest {
  items: AcceptDocumentAnalysisItemRequest[];
}

/** Kalem kabul sonucu — api-contract §20.2 (`AcceptDocumentAnalysisItemResultDto.outcome`). */
export type AcceptDocumentAnalysisItemOutcome = 'accepted' | 'already-accepted' | 'failed';

/**
 * Kalem bazında başarısızlık kodu — api-contract §2/§20.2 (`results[].errorCode`). Küme
 * **KAPALIDIR ve tam olarak ÜÇ değerdir**.
 *
 * ⚠️ `DOCUMENT_ANALYSIS_ITEM_NOT_FOUND` bu kümede **YOKTUR** — o bir **zarf** `404`'üdür
 * (istekteki `itemUuid`'lerden en az biri bu analizin kalemi değilse istek TÜMÜYLE reddedilir,
 * sıfır yazma); kalem sonucu DEĞİLDİR, bu yüzden burada bir üye olarak MODELLENMEZ.
 */
export type AcceptDocumentAnalysisItemErrorCode =
  | 'PERSONNEL_NOT_FOUND'
  | 'DOCUMENT_ANALYSIS_ARTIFACT_MISSING'
  | 'DOCUMENT_ANALYSIS_ARTIFACT_COPY_FAILED';

/**
 * Kalem kabul sonucu — api-contract §20.2 (`AcceptDocumentAnalysisItemResultDto`). İstekteki
 * **her** kalem için **tam olarak bir** sonuç, **istekle aynı sırada**.
 *
 * `documentUuid` / `linkUuid` / `target`: `outcome !== 'failed'` iken **dolu**, aksi `null`.
 * `errorCode`: `outcome === 'failed'` iken **dolu**, aksi `null`. `already-accepted` = kalem
 * **aynı** `target` + `type` + `userUuid` ile zaten kabul edilmiş ve link satırı yaşıyor →
 * **sıfır yazma** (uç bu anlamda idempotenttir; var olan `documentUuid`/`linkUuid` döner).
 *
 * ⚠️ **`linkUuid` oluşan/var olan LİNK satırının KENDİ uuid'idir**
 * (`ContractorDocumentDto.uuid` / `PersonnelDocumentDto.uuid`) ve **silme bununla** yapılır
 * (§13.6/§15.2 `DELETE …/{linkUuid}`) — §8 `documentUuid` **DEĞİLDİR**; `target` `linkUuid`'in
 * hangi tabloda olduğunu söyler (iki link tablosunun uuid uzayları BAĞIMSIZDIR).
 */
export interface AcceptDocumentAnalysisItemResultDto {
  itemUuid: string;
  outcome: AcceptDocumentAnalysisItemOutcome;
  /** §8 dokümanı (indirme §8 `GET /documents/{uuid}`). */
  documentUuid: string | null;
  /** Link satırının KENDİ uuid'i. */
  linkUuid: string | null;
  target: AcceptDocumentAnalysisTarget | null;
  errorCode: AcceptDocumentAnalysisItemErrorCode | null;
}

/**
 * Kabul yanıtı — api-contract §20.2 (`AcceptDocumentAnalysisResultDto`). `acceptedCount` =
 * `outcome` `"accepted"` ya da `"already-accepted"` olan kalem sayısı, `failedCount` =
 * `"failed"` olanlar; `acceptedCount + failedCount == results.length == items.length`.
 * `analysis` = kabul SONRASI **güncel** `DocumentAnalysisDto`'dur (sayaçları yenilenmiş) —
 * istemci sırf sayaç için ayrı bir `GET` ATMAZ.
 */
export interface AcceptDocumentAnalysisResultDto {
  results: AcceptDocumentAnalysisItemResultDto[];
  acceptedCount: number;
  failedCount: number;
  analysis: DocumentAnalysisDto;
}

/**
 * `POST /supplier/contractor/document-analyses/{uuid}/accept` (`update` — kabul de taşeron
 * kaydının bir mutasyonudur; `create` aksiyonu YOKTUR ve eklenemez, §20). Firma/taşeron
 * kimliği **istekte YOKTUR** — kapsam §13.6 deseniyle çözülür (§4b). Analiz yok / başka
 * firmaya ya da başka taşerona ait → `404 DOCUMENT_ANALYSIS_NOT_FOUND`; terminal değilse
 * (`Succeeded` değil) → `409 DOCUMENT_ANALYSIS_NOT_COMPLETE`; `itemUuid`'lerden biri bu
 * analizin kalemi değilse (all-or-nothing) → `404 DOCUMENT_ANALYSIS_ITEM_NOT_FOUND`.
 *
 * ⚠️ **`200` döner, `201` DEĞİL** — bir batch'in okunacak tek bir `value.uuid`'i yoktur (§4a
 * toplu mutation kuralı). **Kısmi başarı VERİDİR, zarf hatası DEĞİLDİR**: her kalemi
 * başarısız olan bir istek bile `isSuccess: true, statusCode: 200` döner. Çağıran
 * `results[].outcome`'a göre dallanır, HTTP durum koduna göre **DEĞİL** — bu dosyanın dönüş
 * tipi (`AcceptDocumentAnalysisResultDto`) HTTP durumunu değil `results[]`'i taşıdığı için bu
 * yanlış dallanma zaten derleme zamanında zorlaştırılır.
 */
export function acceptDocumentAnalysis(uuid: string, body: AcceptDocumentAnalysisRequest) {
  // `uuid` rota parametresinden gelir — kodlanmadan gömülürse yol enjeksiyonuna açılır
  // (bkz. `getDocumentAnalysis` üstündeki yorum, S17 güvenlik incelemesi F3).
  return api.post<AcceptDocumentAnalysisResultDto>(
    `supplier/contractor/document-analyses/${encodeURIComponent(uuid)}/accept`,
    { json: body },
  );
}

// ============================================================================
// 20.3 Analiz listesi (`get`) — api-contract §20.3, YENİ (S19)
// ============================================================================
//
// Sekmesini ya da `uuid`'ini kaybeden istemcinin kurtarma ucu. "Yüklendi, analiz bitti, insan
// henüz kabul etmedi" sistemin OLAĞAN dinlenme durumudur.

/**
 * Liste süzgeci — api-contract §20.3 (`status`). **Tanınmayan bir değer VARSAYILANA
 * (`awaiting-review`) çözülür — `all`'a DEĞİL** (bir yazım hatası sonuç kümesini
 * GENİŞLETEMEZ).
 *
 * `archived` YENİ (S25-A4/A5) — yalnız *etkin arşivdeki* analizler: el ile arşivlenmiş
 * (§20.6) YA DA `Succeeded` **VE** en az bir kalemi var (soft-delete edilmiş — yani
 * reddedilmiş — satırlar DAHİL) **VE** bekleyen kalemi kalmamış. `awaiting-review` /
 * `active` / `all`'ın **üçü de** etkin arşivdeki analizleri artık DIŞLAR — yalnız `all`
 * **hiçbir ek süzgeç uygulamaz** (arşiv dahil GERÇEK ANLAMDA tümü), `archived` yalnız
 * onları döner.
 */
export type DocumentAnalysisListFilter = 'awaiting-review' | 'active' | 'all' | 'archived';

/**
 * Analiz liste parametreleri — api-contract §20.3 + §3 paging. `sortBy`/`sortDir` **YOKTUR** —
 * bu uçta sıralama sabittir (`queuedAt` azalan, tie-break iç `Id`), istemci göndermez.
 */
export interface DocumentAnalysisListParams {
  /** Varsayılan `awaiting-review`. */
  status?: DocumentAnalysisListFilter;
  page?: number;
  pageSize?: number;
}

/**
 * `GET /supplier/contractor/document-analyses` (`get`) — çağıranın **kendi** taşeronunun
 * analizleri. `status=awaiting-review` (varsayılan) = `status = Succeeded` **VE**
 * `pendingItemCount > 0` **VE** etkin arşivde değil; `active` = `status ∈
 * {"Queued","Running"}` **VE** etkin arşivde değil; `all` = GERÇEK ANLAMDA tümü, arşiv DAHİL,
 * hiçbir ek süzgeç uygulanmaz; `archived` (YENİ, S25-A4/A5) = yalnız etkin arşivdeki analizler
 * (yukarıya, `DocumentAnalysisListFilter`'a bakınız). Bağ yoksa → `404 CONTRACTOR_NOT_FOUND`;
 * aktif firmaya atama yoksa → `403 COMPANY_NOT_ACCESSIBLE`.
 *
 * `total` = kapsam uygulanmış, `status` süzgecinden ÖNCEKİ toplam; `filteredTotal` = süzgeç
 * sonrası (§3). **Bu uç `503` DÖNMEZ** ve ne yapılandırmaya ne firma allowlist'ine bakar —
 * analiz TARİHSEL bir kayıttır (§20).
 */
export function listDocumentAnalyses(params: DocumentAnalysisListParams = {}) {
  return api.list<DocumentAnalysisDto>('supplier/contractor/document-analyses', {
    searchParams: {
      status: params.status,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

// ============================================================================
// 20.4 Kalemin reddi + geri alınması (`update`) — api-contract §20.4, YENİ (S24/S25)
// ============================================================================

/**
 * `DELETE /supplier/contractor/document-analyses/{uuid}/items/{itemUuid}` (`update`) — bir
 * kalemi yoksayar (soft-delete + `rejectedAt = <şimdi>`). Kabul edilmiş bir kalem (zaten bir
 * §13.2/§15 belge satırı mint etmiş) → `409 DOCUMENT_ANALYSIS_ITEM_ALREADY_ACCEPTED`; aynı
 * kaleme ikinci çağrı → `404 DOCUMENT_ANALYSIS_ITEM_NOT_FOUND` (silme idempotenttir — global
 * soft-delete filtresi reddedilen satırı bu ucun kendi okumasından düşürür, ayrı bir "zaten
 * reddedildi" kodu yoktur). Reddedilen kalem §20.3'ün sayaçlarında ve kabul yolunda bir daha
 * görünmez — ama (S25) §20.1'in detay ucunda (`GET …/{uuid}`) `rejectedAt` dolu olarak KALIR,
 * bu sayede sayfa yenilendikten sonra da üstü çizili satır + `geri al` gösterilebilir.
 */
export function rejectDocumentAnalysisItem(analysisUuid: string, itemUuid: string) {
  // `analysisUuid` / `itemUuid` rota parametrelerinden gelir — kodlanmadan gömülürse yol
  // enjeksiyonuna açılır (bkz. `getDocumentAnalysis` üstündeki yorum, S17 güvenlik incelemesi F3).
  return api.delete(
    `supplier/contractor/document-analyses/${encodeURIComponent(analysisUuid)}` +
      `/items/${encodeURIComponent(itemUuid)}`,
  );
}

/**
 * `POST /supplier/contractor/document-analyses/{uuid}/items/{itemUuid}/restore` (`update`) —
 * YENİ (S25), reddin **tersi**: `IsDeleted = false`, `rejectedAt = null`. Analiz çözümü §20.1
 * `GET`'in aynı firma+taşeron filtresiyle → `404 DOCUMENT_ANALYSIS_NOT_FOUND`; kalem soft-delete
 * filtresi YOKSAYILARAK çözülür (aksi hâlde reddedilmiş kalem, çözülmesi gereken tam da bu
 * uçta bulunamazdı) → `404 DOCUMENT_ANALYSIS_ITEM_NOT_FOUND`; kalem var ama `rejectedAt` boşsa
 * (bekliyor YA DA zaten kabul edilmiş — ikisi de AYNI kodu üretir, ayrı bir dal yoktur) →
 * `409 DOCUMENT_ANALYSIS_ITEM_NOT_REJECTED`. → `Result<Empty>`; `200` + boş zarf.
 */
export function restoreDocumentAnalysisItem(analysisUuid: string, itemUuid: string) {
  // `analysisUuid` / `itemUuid` rota parametrelerinden gelir — kodlanmadan gömülürse yol
  // enjeksiyonuna açılır (bkz. `getDocumentAnalysis` üstündeki yorum, S17 güvenlik incelemesi F3).
  return api.post(
    `supplier/contractor/document-analyses/${encodeURIComponent(analysisUuid)}` +
      `/items/${encodeURIComponent(itemUuid)}/restore`,
  );
}

// ============================================================================
// 20.5 Analizin tümüyle silinmesi (discard) (`update`) — api-contract §20.5, YENİ (S24/S25-A8)
// ============================================================================

/**
 * `DELETE /supplier/contractor/document-analyses/{uuid}` (`update`) — analizin TÜMÜNDEN
 * vazgeçer. Terminal olmayan (`Queued`/`Running`) bir analiz → `409
 * DOCUMENT_ANALYSIS_NOT_TERMINAL`.
 *
 * **YENİ engel (S25-A8/D4).** Analizin **en az bir** kalemi zaten kabul edilip §8 dokümanı +
 * bağ satırı olarak dosyalanmışsa (`item.acceptedLinkUuid !== null` herhangi bir kalemde) →
 * istek TÜMÜYLE `409 DOCUMENT_ANALYSIS_HAS_ACCEPTED_ITEMS` ile reddedilir, **hiçbir şey
 * değişmez** (ne satır soft-delete edilir ne depoda bir şey silinir). Kabul geri alınamaz bir
 * uygunluk kararıdır; bu analizi listeden kaldırmak isteyen istemci **arşivler** (§20.6,
 * `archiveDocumentAnalysis`), **silmez**.
 *
 * Yukarıdaki engelden geçen bir istek için: analiz + tüm kalemleri soft-delete edilir, analizin
 * sakladığı sayfa görselleri (thumbnail) portalın kendi doküman bucket'ından fiziksel olarak
 * silinir. **YENİ (S25-A8/M9) — kaynak dokümanların koşullu HARD-DELETE'i.** Bu noktada
 * analizin hiçbir kalemi kabul edilmemiştir; başlatmada verilen kaynak dosyalardan **hiçbir
 * canlı bağı** (firma-çaplı — hem §13.2 hem §15.2, yalnız bu analizin kalemleriyle sınırlı
 * DEĞİL) kalmamış olanlar kalıcı olarak (hard-delete) silinir; en az bir canlı bağı olan bir
 * kaynağa dokunulmaz. (S24'te "kaynak dokümanlar hiçbir koşulda silinmez" denirdi — bu M9 ile
 * geçersizdir, yalnız kabul edilmemiş bir analizin bağsız kalan kaynakları için geçerlidir.)
 */
export function discardDocumentAnalysis(uuid: string) {
  // `uuid` rota parametresinden gelir — kodlanmadan gömülürse yol enjeksiyonuna açılır
  // (bkz. `getDocumentAnalysis` üstündeki yorum, S17 güvenlik incelemesi F3).
  return api.delete(`supplier/contractor/document-analyses/${encodeURIComponent(uuid)}`);
}

// ============================================================================
// 20.6 Manuel arşiv (`update`) — api-contract §20.6, YENİ (S25-A4)
// ============================================================================
//
// §20.5'in artık reddettiği (`409 DOCUMENT_ANALYSIS_HAS_ACCEPTED_ITEMS`) bir analizin listeden
// kaldırılmasının kaçış yolu; ayrıca yarım kalan / vazgeçilen bir analiz için de kullanılabilir
// — **terminal olma şartı YOKTUR**. Türkçe etiket `Arşivle` / `Arşivden çıkar`.

/**
 * `POST /supplier/contractor/document-analyses/{uuid}/archive` (`update`) — `IsArchived =
 * true`, `archivedAt = <şu anki UTC>` yazar. Analiz yok / başka taşeron/firma → `404
 * DOCUMENT_ANALYSIS_NOT_FOUND` (§20.1 `GET`'in aynı filtresi). **Durum kısıtı yoktur** —
 * `Queued`/`Running`/`Succeeded`/`Failed` hepsi arşivlenebilir (silmenin aksine, arşivlemek
 * hiçbir canlı worker yazımıyla çakışmaz). Zaten arşivli bir analizi tekrar arşivlemek
 * **idempotenttir** (`archivedAt` yeniden şu anki zamana güncellenir). → `Result<Empty>`;
 * `200` + boş zarf.
 */
export function archiveDocumentAnalysis(uuid: string) {
  // `uuid` rota parametresinden gelir — kodlanmadan gömülürse yol enjeksiyonuna açılır
  // (bkz. `getDocumentAnalysis` üstündeki yorum, S17 güvenlik incelemesi F3).
  return api.post(`supplier/contractor/document-analyses/${encodeURIComponent(uuid)}/archive`);
}

/**
 * `POST /supplier/contractor/document-analyses/{uuid}/unarchive` (`update`) — arşivin tersi:
 * `IsArchived = false`, `archivedAt = null` yazar. Aynı kapsam/çözüm/`404` deseni. **Görünür
 * etkisi olmayabilir** — analiz `Succeeded` VE kalemleri tam kararlıysa (her kalem kabul
 * edilmiş YA DA reddedilmiş, hiçbiri bekleyen değil) §20.3'ün *etkin arşiv* yüklemi türetilmiş
 * ikinci koldan anında yeniden etkin-arşive düşer (`?status=archived`'de görünmeye devam
 * eder) — bu bir hata değildir. `Failed` bir analiz için bu istisna geçerli DEĞİLDİR; orada
 * `unarchive` her zaman gözle görülür bir sonuç üretir. → `Result<Empty>`; `200` + boş zarf.
 */
export function unarchiveDocumentAnalysis(uuid: string) {
  // `uuid` rota parametresinden gelir — kodlanmadan gömülürse yol enjeksiyonuna açılır
  // (bkz. `getDocumentAnalysis` üstündeki yorum, S17 güvenlik incelemesi F3).
  return api.post(`supplier/contractor/document-analyses/${encodeURIComponent(uuid)}/unarchive`);
}
